import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi, type DashboardData } from '@/api';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Card } from '@/components/ui/card';
import { MonthPeriodFilter } from '@/components/ui/MonthPeriodFilter';
import { currentMonthRange, formatBRL, rangeLabel, type DateRange } from '@/lib/utils';

export function DashboardPage() {
  const [period, setPeriod] = useState<DateRange>(() => currentMonthRange());

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', period],
    queryFn: () => reportsApi.dashboard({ from: period.from, to: period.to }),
  });

  return (
    <>
      <PageHeader
        title="Dashboard"
        breadcrumb="Início"
        actions={
          data && (
            <div className="text-sm bg-muted rounded-md px-3 py-1.5">
              Saldo total <span className="font-medium ml-2 text-emerald-700">{formatBRL(data.consolidatedBalance)}</span>
            </div>
          )
        }
      />
      <PageBody>
        <MonthPeriodFilter value={period} onChange={setPeriod} />

        {isLoading && <div className="text-muted-foreground">Carregando…</div>}
        {data && <DashboardContent data={data} period={period} />}
      </PageBody>
    </>
  );
}

function DashboardContent({ data, period }: { data: DashboardData; period: DateRange }) {
  const periodText = rangeLabel(period);
  // Compat: backend novo retorna payablePeriod; backend antigo só payable30.
  const payable = data.payablePeriod ?? data.payable30;
  const receivable = data.receivablePeriod ?? data.receivable30;
  const paid = data.paidPeriod?.total ?? '0';
  const received = data.receivedPeriod?.total ?? '0';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KPI label="Saldo consolidado" value={formatBRL(data.consolidatedBalance)} variant="success"
          subtitle={data.accounts.map((a) => `${a.name}: ${formatBRL(a.balance)}`).join(' · ')} />
        <KPI label={`A receber — ${periodText}`} value={formatBRL(receivable.total)} variant="info"
          subtitle={`${receivable.count} títulos em aberto`} />
        <KPI label={`A pagar — ${periodText}`} value={formatBRL(payable.total)} variant="warning"
          subtitle={`${payable.count} títulos em aberto`} />
        <KPI label="Vencidos (hoje)" value={formatBRL(data.overduePayables.total)} variant="danger"
          subtitle={`${data.overduePayables.count} títulos atrasados`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <KPI label={`Já recebido — ${periodText}`} value={formatBRL(received)} variant="info"
          subtitle="Lançamentos efetivados (entradas confirmadas no período)" />
        <KPI label={`Já pago — ${periodText}`} value={formatBRL(paid)} variant="warning"
          subtitle="Lançamentos efetivados (saídas confirmadas no período)" />
      </div>

      <Card className="p-4">
        <h3 className="font-medium mb-3">Entradas vs Saídas — {periodText}</h3>
        <BarChart series={data.daily} />
      </Card>

      {data.overdue && (data.overdue.receivables.length > 0 || data.overdue.payables.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UpcomingCard
            title="Em atraso — a receber"
            emptyText="Nenhum recebimento em atraso"
            items={data.overdue.receivables}
            amountClass="text-emerald-700"
            highlight="danger"
          />
          <UpcomingCard
            title="Em atraso — a pagar"
            emptyText="Nenhum pagamento em atraso"
            items={data.overdue.payables}
            amountClass="text-rose-700"
            highlight="danger"
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UpcomingCard
          title="Próximos 7 dias — a receber"
          emptyText="Nenhum recebimento nos próximos 7 dias"
          items={data.upcoming.receivables}
          amountClass="text-emerald-700"
        />
        <UpcomingCard
          title="Próximos 7 dias — a pagar"
          emptyText="Nenhum pagamento nos próximos 7 dias"
          items={data.upcoming.payables}
          amountClass="text-rose-700"
        />
      </div>
    </div>
  );
}

function KPI({ label, value, subtitle, variant }: { label: string; value: string; subtitle: string; variant: string }) {
  const colors: Record<string, string> = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    info: 'bg-sky-50 border-sky-200 text-sky-900',
    warning: 'bg-amber-50 border-amber-200 text-amber-900',
    danger: 'bg-rose-50 border-rose-200 text-rose-900',
  };
  return (
    <div className={`rounded-lg border p-4 ${colors[variant]}`}>
      <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-medium mt-1">{value}</div>
      <div className="text-xs mt-2 opacity-80 line-clamp-2">{subtitle}</div>
    </div>
  );
}

type UpcomingItem = {
  id: string;
  description: string;
  amount: string;
  dueDate: string;
  party: { name: string };
  installmentNumber?: number | null;
  installmentTotal?: number | null;
};

function UpcomingCard({
  title, emptyText, items, amountClass, highlight,
}: {
  title: string;
  emptyText: string;
  items: UpcomingItem[];
  amountClass: string;
  /** Destaca o card todo (ex.: vermelho para "Em atraso"). */
  highlight?: 'danger';
}) {
  const total = items.reduce((sum, item) => sum + Number(item.amount), 0);
  const cardClass = highlight === 'danger' ? 'p-4 border-rose-300 bg-rose-50/40' : 'p-4';
  return (
    <Card className={cardClass}>
      <div className="flex justify-between items-baseline mb-3">
        <h3 className="font-medium">
          {title}
          {items.length > 0 && (
            <span className="text-xs text-muted-foreground font-normal ml-2">
              ({items.length})
            </span>
          )}
        </h3>
        {items.length > 0 && (
          <span className={`text-sm font-medium ${amountClass}`}>{formatBRL(total)}</span>
        )}
      </div>
      <ul className="text-sm divide-y">
        {items.map((item) => {
          const isInstallment = item.installmentNumber && item.installmentTotal;
          return (
            <li key={item.id} className="py-2 flex justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="truncate">
                  {item.party.name}
                  {isInstallment && (
                    <span className="text-xs text-muted-foreground font-normal ml-1.5">
                      ({item.installmentNumber}/{item.installmentTotal})
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {item.description} · {new Date(item.dueDate).toLocaleDateString('pt-BR')}
                </div>
              </div>
              <div className={`${amountClass} font-medium whitespace-nowrap`}>{formatBRL(item.amount)}</div>
            </li>
          );
        })}
        {items.length === 0 && (
          <li className="py-3 text-muted-foreground text-center">{emptyText}</li>
        )}
      </ul>
    </Card>
  );
}

/**
 * Gráfico de barras Entradas vs Saídas com tooltip interativo no hover.
 * Implementado em SVG puro (sem libs) — coordenadas viewBox 0-700 x 0-220.
 */
function BarChart({ series }: { series: { date: string; in: string; out: string }[] }) {
  const [hover, setHover] = useState<number | null>(null);

  const max = useMemo(
    () => Math.max(...series.flatMap((s) => [Number(s.in), Number(s.out)]), 1),
    [series],
  );

  if (!series.length) return <div className="text-muted-foreground text-sm">Sem dados no período</div>;

  const groupWidth = 640 / Math.max(series.length, 1);
  const barWidth = Math.max(6, groupWidth - 4) / 2 - 1;
  const chartTop = 20;
  const chartBottom = 180;
  const chartHeight = chartBottom - chartTop;

  // Linhas de grade horizontais (4 níveis: 25/50/75/100%)
  const gridLines = [0.25, 0.5, 0.75, 1].map((p) => ({
    y: chartBottom - chartHeight * p,
    value: max * p,
  }));

  const formatDate = (iso: string) => {
    // iso vem como yyyy-MM-dd (BR date key) — exibe dd/MM
    const [, m, d] = iso.split('-');
    return `${d}/${m}`;
  };

  // Mostra ticks no eixo X só a cada N barras pra não poluir
  const tickEvery = Math.max(1, Math.ceil(series.length / 10));

  return (
    <div className="relative w-full">
      <svg
        viewBox="0 0 700 220"
        className="w-full"
        onMouseLeave={() => setHover(null)}
      >
        {/* Grade horizontal */}
        {gridLines.map((g, i) => (
          <g key={i}>
            <line x1="40" y1={g.y} x2="680" y2={g.y} stroke="#e2e8f0" strokeDasharray="2 3" />
            <text x="36" y={g.y + 3} fontSize="9" textAnchor="end" fill="#64748b">
              {g.value >= 1000 ? `${(g.value / 1000).toFixed(0)}k` : g.value.toFixed(0)}
            </text>
          </g>
        ))}
        <line x1="40" y1={chartBottom} x2="680" y2={chartBottom} stroke="#cbd5e1" />

        {series.map((s, i) => {
          const x = 50 + i * groupWidth;
          const inH = (Number(s.in) / max) * chartHeight;
          const outH = (Number(s.out) / max) * chartHeight;
          const isHover = hover === i;
          return (
            <g key={s.date}>
              {/* Barras */}
              <rect x={x} y={chartBottom - inH} width={barWidth} height={inH}
                fill="#10b981" opacity={hover === null || isHover ? 1 : 0.45} />
              <rect x={x + barWidth + 1} y={chartBottom - outH} width={barWidth} height={outH}
                fill="#f43f5e" opacity={hover === null || isHover ? 1 : 0.45} />
              {/* Hit area do grupo (transparente, captura mouse) */}
              <rect
                x={x - 2}
                y={chartTop}
                width={groupWidth}
                height={chartHeight}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                style={{ cursor: 'pointer' }}
              />
              {/* Tick do eixo X */}
              {i % tickEvery === 0 && (
                <text x={x + barWidth} y={chartBottom + 12} fontSize="9" textAnchor="middle" fill="#64748b">
                  {formatDate(s.date)}
                </text>
              )}
            </g>
          );
        })}

      </svg>

      {/* Legenda — fora do SVG para não sobrepor barras altas */}
      <div className="flex items-center justify-center gap-6 mt-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" />
          Entradas
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm bg-rose-500" />
          Saídas
        </div>
      </div>

      {/* Tooltip HTML overlay — posicionado em % do viewBox para acompanhar responsividade */}
      {hover !== null && (() => {
        const s = series[hover];
        const groupCenterPct = ((50 + hover * groupWidth + barWidth) / 700) * 100;
        // Mantém o tooltip dentro do container quando estiver perto das bordas
        const left = Math.min(Math.max(groupCenterPct, 8), 92);
        const transform = groupCenterPct > 80
          ? 'translate(-100%, 0)'
          : groupCenterPct < 20
            ? 'translate(0, 0)'
            : 'translate(-50%, 0)';
        return (
          <div
            className="pointer-events-none absolute top-0 z-10 rounded-md border bg-white shadow-lg px-3 py-2 text-xs"
            style={{ left: `${left}%`, transform }}
          >
            <div className="font-medium mb-1">
              {new Date(s.date + 'T12:00:00').toLocaleDateString('pt-BR', {
                day: '2-digit', month: 'short', year: 'numeric',
              })}
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-sm bg-emerald-500" />
              <span className="text-muted-foreground">Entradas:</span>
              <span className="font-medium text-emerald-700">{formatBRL(s.in)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-sm bg-rose-500" />
              <span className="text-muted-foreground">Saídas:</span>
              <span className="font-medium text-rose-700">{formatBRL(s.out)}</span>
            </div>
            <div className="mt-1 pt-1 border-t flex items-center gap-2">
              <span className="text-muted-foreground">Saldo:</span>
              <span className={`font-medium ${Number(s.in) - Number(s.out) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                {formatBRL(Number(s.in) - Number(s.out))}
              </span>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
